import {
  Badge,
  Box,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Link,
  Text,
} from '@optiaxiom/react';

import { MarkdownText } from '@frontend/components/MarkdownText';
import { StepExecution } from '@frontend/types/stepExecution';
import { formatJsonForMarkdown } from '@frontend/utils/app-companion';

import { formatExecutionTime, getStatusColor } from '../../utils/executionUtils';
import { ExecutionOutputDisplay } from '../specialized-agent/shared';

import { styles } from './ExecutionHistoryModal.css';

type ExecutionHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  executions: StepExecution[];
  onSelectExecution: (execution: StepExecution) => void;
  selectedExecutionId?: string;
  stepId: string;
};

export function ExecutionHistoryModal({
  isOpen,
  onClose,
  executions,
  onSelectExecution,
  selectedExecutionId,
  stepId,
}: ExecutionHistoryModalProps) {
  const handleExecutionSelect = (execution: StepExecution) => {
    onSelectExecution(execution);
    onClose();
  };
  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent h="full" onEscapeKeyDown={onClose} size="md">
        <DialogHeader description={`Step ${stepId}`}>Execution History</DialogHeader>
        <DialogBody>
          {executions.length === 0 ? (
            <Box p="24">
              <Text>No executions found for this step.</Text>
            </Box>
          ) : (
            <Box h="full">
              <Box className={styles.scrollContainer} p="8">
                <Flex flexDirection="column" className={styles.executionContainer}>
                  {executions.map((execution, index) => (
                    <Card
                      key={execution.id || index}
                      className={
                        selectedExecutionId === execution.id ? styles.executionButtonSelected : ''
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      <CardHeader
                        h="full"
                        addonAfter={
                          <Flex alignItems="end" flexDirection="row" gap="8">
                            <Box>
                              <Badge intent={getStatusColor(execution.status)}>
                                {execution.status}
                              </Badge>
                            </Box>
                            <Box>
                              <Link
                                onClick={() => handleExecutionSelect(execution)}
                                cursor="pointer"
                              >
                                View
                              </Link>
                            </Box>
                          </Flex>
                        }
                        description={
                          execution.error_message
                            ? `Error: ${execution.error_message}`
                            : formatExecutionTime(execution.start_time, execution.end_time)
                        }
                      >
                        <Disclosure>
                          <DisclosureTrigger>
                            <Text fontWeight="500">Execution #{index + 1}</Text>
                          </DisclosureTrigger>
                          <DisclosureContent>
                            {execution.parameters &&
                            Object.keys(execution.parameters).length > 0 ? (
                              <Field label="Parameters">
                                <Box border="1" borderColor="border.default" rounded="md">
                                  <Box bg="bg.default" p="12">
                                    <MarkdownText
                                      text={formatJsonForMarkdown(execution.parameters)}
                                    />
                                  </Box>
                                </Box>
                              </Field>
                            ) : null}
                            {execution.result && (
                              <Flex flexDirection="column" gap="16" px="16">
                                <Field label="Agent Response">
                                  <ExecutionOutputDisplay
                                    result={execution.result}
                                    showStatus={false}
                                    emptyMessage="No output available."
                                    emptySubMessage="Execution did not produce any results."
                                  />
                                </Field>
                              </Flex>
                            )}
                          </DisclosureContent>
                        </Disclosure>
                      </CardHeader>
                    </Card>
                  ))}
                </Flex>
              </Box>
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose onClick={onClose}>Close</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
